import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AppService, ProductTranslations } from '../../services/app.service';
import { Product, ProductHeadline, ProductsService } from '../../services/products.service';
import { MatSort } from '@angular/material/sort';
import { QuoteService } from '../../services/quote.service';
import { openConfirmationDialog } from '../confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { ProductEditEvent, ProductEditType, ProductListService } from '../product-list/product-list.service';

const COMMON_COLUMNS = ['product-group', 'image', 'description', 'partNumber', 'quantity', 'materialGroup'];

type ProductFormControlType = {
    selected: FormControl<boolean>;
    optional: FormControl<boolean>;
    quantity: FormControl<number>;
};

type EditableProduct = Product & { formGroup: FormGroup<ProductFormControlType> };

@Component({
    selector: 'app-product-table',
    templateUrl: './product-table.component.html',
    styleUrls: ['./product-table.component.scss'],
})
export class ProductTableComponent implements OnInit {
    constructor(
        private quoteService: QuoteService,
        private translateService: TranslateService,
        private dialog: MatDialog,
        private appService: AppService,
        public productService: ProductsService,
        private productListService: ProductListService
    ) {}

    private sampleOfferId: string;

    @ViewChild(MatSort)
    public sort: MatSort;

    @Input()
    public dataSource: MatTableDataSource<Product>;

    @Input()
    public set sampleOffer(value: string) {
        this.sampleOfferId = value;
        this.init();
    }

    public columnsToDisplay: string[];

    private init() {
        if (this.dataSource) {
            this.dataSource.sort = this.sort;
        }

        const conditionalColumns = [
            ...(this.appService.isOptionalPositionsEnabled ? ['isOptional'] : []),
            ...(this.canDelete ? ['actions'] : []),
        ];
        this.columnsToDisplay = [...(this.sampleOfferId ? ['select'] : []), ...COMMON_COLUMNS, ...conditionalColumns];

        this.clearEdits();
    }

    public ngOnInit() {
        this.init();
    }

    public get hasNoConfiguration() {
        return !this.quoteService.hasConfiguration();
    }

    public get hasSampleOffer() {
        return !!this.sampleOfferId;
    }

    public get canChangeQuantity() {
        return this.hasNoConfiguration || (this.hasSampleOffer && this.appService.getSalesOrg() === '8000');
    }

    public get canDelete() {
        return this.hasNoConfiguration || this.hasSampleOffer;
    }

    public chooseTranslation(productTranslations?: ProductTranslations) {
        return this.appService.chooseTranslation(productTranslations);
    }

    public deleteProductGroup(productGroup: ProductHeadline) {
        openConfirmationDialog(
            this.dialog,
            this.translateService.instant('SUMMARY.DIALOG.DELETE_PRODUCT_GROUP.TITLE'),
            this.translateService.instant('SUMMARY.DIALOG.DELETE_PRODUCT_GROUP.MESSAGE', {
                name: productGroup.groupNameTranslation,
            }),
            this.translateService.instant('COMMON.DIALOG.CONFIRM'),
            this.translateService.instant('COMMON.DIALOG.CANCEL'),
            (confirmed) => {
                if (confirmed) {
                    this.editProduct(
                        ...productGroup.children.map((productId) => ({ type: ProductEditType.delete, productId }))
                    );
                }
            }
        );
    }

    public deleteProduct(product: EditableProduct) {
        const productId = product.material;
        openConfirmationDialog(
            this.dialog,
            this.translateService.instant('SUMMARY.DIALOG.DELETE_PRODUCT.TITLE'),
            this.translateService.instant('SUMMARY.DIALOG.DELETE_PRODUCT.MESSAGE', { name: productId }),
            this.translateService.instant('COMMON.DIALOG.CONFIRM'),
            this.translateService.instant('COMMON.DIALOG.CANCEL'),
            (confirmed) => {
                if (confirmed) {
                    this.editProduct({ type: ProductEditType.delete, productId });

                    // when a product is deleted, perform a check on the group of products ( = the "main" product + its alternatives)

                    const groupOfProductWithAlternatives = [
                        this.getParentProduct(product),
                        ...this.getAlternativesOf(product),
                        ...this.getSiblingProducts(product),
                    ].filter(Boolean);
                    // if there is one ore more product(s) of this group, at least one must be marked as selected
                    if (
                        groupOfProductWithAlternatives.length &&
                        !groupOfProductWithAlternatives.filter((p) => p.selected).length
                    ) {
                        // mark the first one of the group as selected
                        const targetToSelect = groupOfProductWithAlternatives[0];
                        this.setSelected(targetToSelect, true);
                    }
                }
            }
        );
    }

    public canDeleteProductGroup(productGroup: ProductHeadline) {
        const products = this.allProducts.filter((p) => productGroup.children.includes(p.material));
        return products.every((p) => p.canBeDeleted);
    }

    private editProduct(...events: ProductEditEvent[]) {
        const current = this.productListService.productListEdits$.value || [];
        this.productListService.productListEdits$.next([...current, ...events]);
    }

    public get canEdit(): boolean {
        return (
            this.canDelete ||
            this.dataSource?.data?.some((value: EditableProduct) => {
                return (
                    value.formGroup &&
                    Object.keys(value.formGroup.controls).some((key) => {
                        const control: FormControl = value.formGroup.controls[key];
                        return control?.enabled;
                    })
                );
            })
        );
    }

    public get hasEdits() {
        return this.productListService.productListEdits$.value.length > 0;
    }

    public clearEdits() {
        this.productListService.clearEdits();
    }

    public select(product: EditableProduct) {
        const selected = product.formGroup.controls.selected.value;
        this.editProduct({ type: ProductEditType.select, productId: product.material, newValue: selected });

        if (product.alternativeFor) {
            // when alternative is selected, the parent has to be unselected and vice versa
            const parent = this.getParentProduct(product);

            if (parent) {
                // parent could be deleted
                this.setSelected(parent, !selected);
            }
            if (selected) {
                // only one alternative can be selected at the same time, so in this case also de-select all siblings
                const siblings = this.getSiblingProducts(product);
                siblings.forEach((element: EditableProduct) => {
                    this.setSelected(element, !selected);
                });
            }
        } else {
            // deselect all alternatives
            const alternatives = this.getAlternativesOf(product);
            alternatives.forEach((a) => this.setSelected(<EditableProduct>a, !selected));
        }
    }

    private setSelected(product: EditableProduct, selected: boolean) {
        product.formGroup.controls.selected.setValue(selected);
        this.editProduct({ type: ProductEditType.select, productId: product.material, newValue: selected });
    }

    public isOrHasAlternative(product: EditableProduct): boolean {
        return product.material && !!(product.alternativeFor || this.getAlternativesOf(product).length);
    }

    private getParentProduct(product: EditableProduct): EditableProduct | undefined {
        return product.alternativeFor ? this.allProducts.find((p) => p.material === product.alternativeFor) : undefined;
    }

    private getAlternativesOf(product: EditableProduct): EditableProduct[] {
        if (product.alternativeFor) {
            // product itself is an alternative, so it can't have alternatives
            return [];
        }
        return this.allProducts.filter((p) => p.alternativeFor === product.material);
    }

    private getSiblingProducts(product: EditableProduct): EditableProduct[] {
        return product.alternativeFor
            ? this.allProducts.filter(
                  (p) => p.alternativeFor === product.alternativeFor && p.material !== product.material
              )
            : [];
    }

    private get allProducts(): EditableProduct[] {
        return <EditableProduct[]>this.dataSource.data;
    }

    public changeQuantity(product: EditableProduct) {
        const quantity = product.formGroup.controls.quantity.value;
        this.editProduct({ type: ProductEditType.quantity, productId: product.material, newValue: quantity });
    }

    public setOptional(product: EditableProduct) {
        const optional = product.formGroup.controls.optional.value;
        this.editProduct({ type: ProductEditType.optional, productId: product.material, newValue: optional });
    }
}
